<template>
  <div class="page-container">
    <div class="page-title-container flex items-center">
      <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'employee-list' })">
        <i class="ri-arrow-left-line"></i> Volver
      </wz-button>
      <h1 class="page-title mb-0">Carga masiva de derechohabientes</h1>
    </div>

    <!-- Stepper -->
    <div class="flex justify-center mb-4">
      <div class="flex items-center">
        <div :class="['bulk-step', step >= 1 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">1</span><span class="bulk-step-label">Subir</span>
        </div>
        <div class="bulk-step-line"></div>
        <div :class="['bulk-step', step >= 2 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">2</span><span class="bulk-step-label">Validar</span>
        </div>
        <div class="bulk-step-line"></div>
        <div :class="['bulk-step', step >= 3 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">3</span><span class="bulk-step-label">Cargar</span>
        </div>
      </div>
    </div>

    <!-- PASO 1: subir -->
    <div v-if="step === 1">
      <wz-card>
        <div class="flex justify-between items-center mb-4">
          <p class="text-muted mb-0">Importe derechohabientes desde el Excel del T-Registro (.xlsx)</p>
          <wz-button variant="light" @click="downloadTemplate" :disabled="downloadingTemplate">
            <i class="ri-download-line mr-1"></i>
            {{ downloadingTemplate ? 'Descargando...' : 'Descargar plantilla' }}
          </wz-button>
        </div>

        <div class="bulk-dropzone text-center" @click="$refs.fileInput.click()" @dragover.prevent @drop.prevent="onDrop">
          <i class="ri-upload-cloud-line" style="font-size: 3rem; color: #adb5bd;"></i>
          <p class="mt-2 mb-1">Arrastre un archivo .xlsx aquí o haga clic para seleccionar</p>
          <small class="text-muted">Formato T-Registro | Máximo: 500 filas | Tamaño máximo: 5 MB</small>
        </div>
        <input type="file" ref="fileInput" accept=".xlsx" style="display: none;" @change="onFileSelected" />

        <div v-if="selectedFile" class="mt-3 flex items-center justify-between border rounded p-3">
          <div>
            <i class="ri-file-excel-line text-success mr-2" style="font-size: 1.5rem;"></i>
            <strong>{{ selectedFile.name }}</strong>
            <small class="text-muted ml-2">({{ formatFileSize(selectedFile.size) }})</small>
          </div>
          <div>
            <wz-button variant="light" size="sm" class="mr-2" @click="clearFile">Quitar</wz-button>
            <wz-button variant="primary" @click="processFile" :disabled="processing">
              {{ processing ? 'Procesando...' : 'Validar archivo' }}
            </wz-button>
          </div>
        </div>

        <wz-alert variant="info" icon="ri-information-line" class="mt-4">
          El titular (documento) debe existir en el sistema. El cónyuge/conviviente se deriva del documento
          sustentatorio. Los hijos mayores de edad se cargan con un aviso para revisar discapacidad.
        </wz-alert>
      </wz-card>
    </div>

    <!-- PASO 2: preview -->
    <div v-if="step === 2 && validationResult">
      <div class="flex flex-wrap mb-4 -mx-2">
        <div class="w-full md:w-1/4 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700;">{{ validationResult.totalRows }}</div>
            <div class="text-muted text-sm">Total de filas</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700; color: #28a745;">{{ validationResult.validRows }}</div>
            <div class="text-muted text-sm">Válidas</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700; color: #dc3545;">{{ validationResult.errorRows }}</div>
            <div class="text-muted text-sm">Con errores</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700; color: #f0ad4e;">{{ validationResult.warningRows }}</div>
            <div class="text-muted text-sm">Con avisos</div>
          </div>
        </div>
      </div>

      <wz-card>
        <div class="overflow-x-auto">
          <table class="wz-table w-full">
            <thead>
              <tr>
                <th>Fila</th><th>Derechohabiente</th><th>Parentesco</th><th>Estado</th><th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in validationResult.rows" :key="r.rowNumber" :class="{ 'bulk-row-error': !r.valid }">
                <td>{{ r.rowNumber }}</td>
                <td>{{ r.fullName }}</td>
                <td>{{ r.relationshipType || '-' }}</td>
                <td>
                  <wz-badge :variant="r.valid ? 'success-solid' : 'danger-solid'" pill>
                    {{ r.valid ? 'Válida' : 'Con error' }}
                  </wz-badge>
                </td>
                <td>
                  <div v-for="(e, i) in r.errors" :key="'e' + i" class="text-danger text-sm">{{ e }}</div>
                  <div v-for="(w, i) in r.warnings" :key="'w' + i" class="text-warning text-sm">
                    <i class="ri-alert-line"></i> {{ w }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-between mt-4">
          <wz-button variant="light" @click="resetAll">Cancelar</wz-button>
          <wz-button variant="primary" :disabled="validationResult.validRows === 0 || confirming" @click="askConfirmImport">
            Confirmar carga ({{ validationResult.validRows }} válidas)
          </wz-button>
        </div>
      </wz-card>
    </div>

    <!-- PASO 3: resultado -->
    <div v-if="step === 3">
      <wz-card>
        <div v-if="importResult">
          <div class="text-center mb-4">
            <i class="ri-checkbox-circle-line text-success" style="font-size: 3rem;"></i>
            <h4 class="mt-2">Carga finalizada</h4>
            <p class="text-muted">
              {{ importResult.createdCount }} creados · {{ importResult.failed.length }} con error
            </p>
          </div>

          <wz-alert v-if="importResult.warnings && importResult.warnings.length" variant="warning" icon="ri-alert-line" class="mb-3">
            <strong>Avisos:</strong>
            <div v-for="(w, i) in importResult.warnings" :key="'iw' + i" class="text-sm">
              Fila {{ w.row }}: {{ w.message }}
            </div>
          </wz-alert>

          <wz-alert v-if="importResult.failed && importResult.failed.length" variant="danger" icon="ri-close-line" class="mb-3">
            <strong>Filas con error:</strong>
            <div v-for="(f, i) in importResult.failed" :key="'if' + i" class="text-sm">
              Fila {{ f.row }}: {{ f.message }}
            </div>
          </wz-alert>
        </div>
        <div v-else class="text-center">
          <i class="ri-error-warning-line text-danger" style="font-size: 3rem;"></i>
          <h4 class="mt-2">Error al cargar</h4>
          <p class="text-muted">{{ importError }}</p>
        </div>

        <div class="flex justify-center mt-4">
          <wz-button variant="light" class="mr-2" @click="$router.push({ name: 'employee-list' })">Ir a colaboradores</wz-button>
          <wz-button variant="primary" @click="resetAll">Cargar otro archivo</wz-button>
        </div>
      </wz-card>
    </div>

    <!-- Modal confirmar -->
    <wz-modal v-model="confirmImportModal" title="Confirmar carga">
      <p>Se cargarán <strong>{{ validationResult ? validationResult.validRows : 0 }}</strong> derechohabientes válidos.
        Las filas con error se omitirán. ¿Desea continuar?</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="confirmImportModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="confirmImport">Confirmar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FamilyBulkUploadService } from '@/core/services'
import fileDownload from 'js-file-download'

export default {
  name: 'FamilyBulkUpload',
  data() {
    return {
      step: 1,
      selectedFile: null,
      processing: false,
      confirming: false,
      downloadingTemplate: false,
      validationResult: null,
      importResult: null,
      importError: null,
      confirmImportModal: false,
    }
  },
  methods: {
    async downloadTemplate() {
      try {
        this.downloadingTemplate = true
        const resp = await FamilyBulkUploadService.downloadTemplate()
        fileDownload(resp.data || resp, 'plantilla-derechohabientes.xlsx')
      } catch (e) {
        Alert.error('Error al descargar la plantilla')
      } finally {
        this.downloadingTemplate = false
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) this.selectedFile = file
    },
    onDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file && file.name.endsWith('.xlsx')) {
        this.selectedFile = file
      } else {
        Alert.error('Solo se aceptan archivos .xlsx')
      }
    },
    clearFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    async processFile() {
      if (!this.selectedFile) return
      if (this.selectedFile.size > 5 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 5 MB')
        return
      }
      try {
        this.processing = true
        this.$store.dispatch('app/loading', true)
        const resp = await FamilyBulkUploadService.validate(this.selectedFile)
        this.validationResult = resp.payload
        this.step = 2
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al procesar el archivo')
      } finally {
        this.processing = false
        this.$store.dispatch('app/loading', false)
      }
    },
    askConfirmImport() {
      this.confirmImportModal = true
    },
    async confirmImport() {
      this.confirmImportModal = false
      try {
        this.confirming = true
        this.$store.dispatch('app/loading', true)
        const resp = await FamilyBulkUploadService.confirm(this.selectedFile)
        this.importResult = resp.payload
        this.step = 3
      } catch (e) {
        this.importError = e.errors ? e.errors.message : 'Error inesperado al cargar'
        this.importResult = null
        this.step = 3
      } finally {
        this.confirming = false
        this.$store.dispatch('app/loading', false)
      }
    },
    resetAll() {
      this.step = 1
      this.selectedFile = null
      this.validationResult = null
      this.importResult = null
      this.importError = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
  },
}
</script>

<style scoped>
.bulk-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.bulk-dropzone:hover { border-color: #007bff; }
.bulk-step { display: flex; flex-direction: column; align-items: center; opacity: 0.4; }
.bulk-step-active { opacity: 1; }
.bulk-step-number {
  width: 32px; height: 32px; border-radius: 50%; background: #007bff; color: white;
  display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px;
}
.bulk-step-label { font-size: 12px; margin-top: 4px; color: #333; }
.bulk-step-line { width: 60px; height: 2px; background: #dee2e6; margin: 0 8px 18px; }
.bulk-row-error { background-color: #fde8e8 !important; }
</style>
